/**
 * All property types supported by the backend
 * Categories: Residential, Commercial, Land, Hospitality, Institutional
 */
export type PropertyType =
  // Residential
  | 'APARTMENT' | 'VILLA' | 'FLOOR' | 'RESIDENTIAL_BUILDING'
  | 'RESIDENTIAL_LAND' | 'REST_HOUSE' | 'CHALET' | 'ROOM'
  | 'TOWNHOUSE' | 'STUDIO' | 'PENTHOUSE' | 'DUPLEX'
  // Commercial
  | 'OFFICE' | 'COMMERCIAL_BUILDING' | 'WAREHOUSE' | 'COMMERCIAL_LAND'
  | 'INDUSTRIAL_LAND' | 'FARM' | 'AGRICULTURAL_LAND' | 'MALL'
  | 'HOTEL' | 'WORKSHOP' | 'FACTORY' | 'SCHOOL' | 'HEALTH_CENTER'
  | 'STATION' | 'SHOWROOM'
  // Legacy (frontend compatibility)
  | 'HOUSE' | 'CONDO' | 'LAND' | 'COMMERCIAL' | 'BUILDING'
  // Lowercase variants
  | 'House' | 'Apartment' | 'Condo' | 'Townhouse' | 'Studio' | 'Penthouse';

export interface NeighborhoodFactor { key: 'schools' | 'services' | 'healthcare' | 'transport' | 'religious'; score: number; presentFlags: string[]; }
export interface Neighborhood { hasData: boolean; overallScore: number; factors: NeighborhoodFactor[]; }
export interface NearbyLandmark { key: string; nameEn: string; nameAr: string; type: 'religious' | 'attraction' | 'heritage'; distanceKm: number; lat: number; lng: number; }

export interface Property {
  id: string;
  title: string;
  titleAr?: string; // Arabic title for bilingual support
  price: number;
  bedrooms: number;
  bathrooms: number;
  sqft: number;
  address: string;
  addressAr?: string; // Arabic address for bilingual support
  city: string;
  cityAr?: string; // Arabic city name for bilingual support
  district?: string;
  districtAr?: string;
  state: string;
  stateAr?: string; // Arabic state/region name for bilingual support
  zipCode: string;
  latitude: number;
  longitude: number;
  imageUrl: string;
  galleryImages?: string[];
  description: string;
  descriptionAr?: string;
  propertyType: PropertyType;
  listingType?: 'SALE' | 'RENT' | 'SHORT_RENT';
  rentPeriod?: 'monthly' | 'yearly' | 'daily' | 'weekly'; // For rental properties
  yearBuilt?: number;
  parking: boolean;
  petFriendly: boolean;
  furnished: boolean;
  utilities: string[];
  amenities: string[];
  agentName: string;
  agentNameAr?: string; // Arabic transliteration of broker / agent name
  agentPhone: string;
  agentEmail: string;
  companyId?: string | null;
  companyName?: string | null;
  companyNameAr?: string | null;
  companySlug?: string | null;
  dateAdded: string;
  status: 'available' | 'pending' | 'sold' | 'rented';
  featured: boolean;
  pricePerSqft: number;
  lastUpdated: string;
  // Engagement counters from the API — drive the "الأكثر طلباً" badge
  viewCount?: number;
  inquiryCount?: number;
  favoriteCount?: number;
  parkingSpaces?: number;
  coveredParking?: boolean;
  virtualTourUrl?: string | null;
  hasVirtualTour?: boolean;
  ownerId?: string;  // The user ID of the property owner
  // AI Data Engine fields
  pricePerSqm?: number;
  orientation?: string;
  orientationAr?: string;
  deedStatus?: string;
  deedStatusAr?: string;
  ownerType?: string;
  ownerTypeAr?: string;
  constructionStatus?: string;
  constructionStatusAr?: string;
  adLicenseNumber?: string;
  deedNumber?: string;
  neighborhoodScore?: number;
  walkabilityScore?: number;
  conditionRating?: number;
  // Unit-specific fields (apartments, studios, condos)
  apartmentNumber?: string;  // رقم الشقة / Unit Number
  floorLevel?: number;       // الدور / Floor Level
  // Building-specific fields (villas, hotels, commercial)
  floors?: number;           // عدد الطوابق / Number of floors in building
  // Living room field
  livingRooms?: number;      // غرف المعيشة / Number of living rooms
  // AI search: short "why this matches" explanation (from semantic/hybrid search)
  matchExplanation?: string;
  // Property is published by Safaqat24 on behalf of a private owner under our
  // FAL license. Triggers a notice on the public detail page.
  falLicensed?: boolean;
  neighborhood?: Neighborhood;
  nearbyLandmarks?: NearbyLandmark[];
  floorPlanImages?: string[];
  videoUrl?: string | null;
  // REGA / details-page fields (round-tripped from Property columns + metadata).
  propertyAge?: number | null;
  residentialType?: 'families' | 'singles' | null;
  verified?: boolean;
  verifiedAt?: string | null;
  streetWidth?: number | null;
  planNumber?: string | null;
  landNumber?: string | null;
  warranty?: string | null;
  notes?: string | null;
  falLicenseNumber?: string | null;
  licenseIssueDate?: string | null;
  licenseExpiryDate?: string | null;
  adChannels?: string[];
  encumbrance?: boolean | null;
  saudiBuildingCodeCompliant?: boolean | null;
  mortgaged?: boolean | null;
  restricted?: boolean | null;
  // Display-only ownership boundaries (admin/import-seeded; never set by the form).
  boundaries?: Array<{ name: string; length: string; detail?: string }>;
  // Construction progress (shown when not ready). All optional.
  constructionStage?: string | null;
  constructionProgress?: number | null;
  constructionStartDate?: string | null;
  expectedCompletionDate?: string | null;
}

export const calculatePricePerSqft = (price: number, sqft: number): number => {
  return Math.round(price / sqft);
};

export const mockProperties: Property[] = [
  {
    id: 'ksa-riyadh-01',
    title: 'فيلا عصرية مع حديقة خاصة',
    price: 2850000,
    bedrooms: 5,
    bathrooms: 6,
    sqft: 4650,
    address: 'حي الياسمين، شارع أنس بن مالك',
    city: 'الرياض',
    state: 'منطقة الرياض',
    zipCode: '13325',
    latitude: 24.8327,
    longitude: 46.6288,
    imageUrl: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=80',
    galleryImages: [
      'https://images.unsplash.com/photo-1613553493351-62fae7753d3a?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&w=1200&q=80'
    ],
    description: 'فيلا حديثة بتصميم مفتوح ومساحات واسعة للمعيشة، تضم حديقة خارجية ومسبحاً داخلياً مع غرفة للخادمة وساحة لعب للأطفال.',
    propertyType: 'House',
    yearBuilt: 2022,
    parking: true,
    coveredParking: true,
    parkingSpaces: 4,
    petFriendly: true,
    furnished: false,
    utilities: ['الكهرباء', 'المياه', 'الإنترنت'],
    amenities: ['حديقة خاصة', 'مسبح داخلي', 'غرفة خادمة', 'نظام أمني متكامل'],
    agentName: 'سارة السعود',
    agentPhone: '+966 50 123 4567',
    agentEmail: 'sara.alssaud@safaqat24.sa',
    dateAdded: '2025-01-18',
    status: 'available',
    featured: true,
    pricePerSqft: calculatePricePerSqft(2850000, 4650),
    lastUpdated: '2025-02-10',
    virtualTourUrl: 'https://pannellum.org/images/alma.jpg',
    hasVirtualTour: true
  },
  {
    id: 'ksa-jeddah-02',
    title: 'شقة مطلة على البحر في أبراج الجوهرة',
    price: 1650000,
    bedrooms: 3,
    bathrooms: 3,
    sqft: 2250,
    address: 'طريق الكورنيش، حي الشاطئ',
    city: 'جدة',
    state: 'منطقة مكة المكرمة',
    zipCode: '21553',
    latitude: 21.6165,
    longitude: 39.1217,
    imageUrl: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=80',
    galleryImages: [
      'https://images.unsplash.com/photo-1527030280862-64139fba04ca?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1200&q=80'
    ],
    description: 'شقة فاخرة بواجهات زجاجية وإطلالة بانورامية على البحر الأحمر، مع خدمات فندقية وموقفين مخصصين للسيارات.',
    propertyType: 'Apartment',
    yearBuilt: 2021,
    parking: true,
    coveredParking: true,
    parkingSpaces: 2,
    petFriendly: false,
    furnished: true,
    utilities: ['الكهرباء', 'المياه', 'الإنترنت', 'التكييف المركزي'],
    amenities: ['صالة رياضية', 'مسبح مشترك', 'خدمة استقبال على مدار الساعة', 'منطقة ألعاب أطفال'],
    agentName: 'محمد باوزير',
    agentPhone: '+966 53 987 6543',
    agentEmail: 'm.bawazeer@safaqat24.sa',
    dateAdded: '2025-02-02',
    status: 'available',
    featured: true,
    pricePerSqft: calculatePricePerSqft(1650000, 2250),
    lastUpdated: '2025-02-12'
  },
  {
    id: 'ksa-dammam-03',
    title: 'تاون هاوس عائلي بالقرب من الواجهة البحرية',
    price: 1120000,
    bedrooms: 4,
    bathrooms: 4,
    sqft: 3120,
    address: 'حي المزروعية، شارع الأمير نايف',
    city: 'الدمام',
    state: 'المنطقة الشرقية',
    zipCode: '32416',
    latitude: 26.4365,
    longitude: 50.0888,
    imageUrl: 'https://images.unsplash.com/photo-1502005097973-6a7082348e28?auto=format&fit=crop&w=1200&q=80',
    galleryImages: [
      'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1522156373667-4c7234bbd804?auto=format&fit=crop&w=1200&q=80'
    ],
    description: 'تاون هاوس بثلاثة طوابق مع فناء خلفي واسع وغرفة معيشة مزدوجة الارتفاع، قريب من المدارس الدولية والمراكز التجارية.',
    propertyType: 'Townhouse',
    yearBuilt: 2019,
    parking: true,
    coveredParking: true,
    parkingSpaces: 3,
    petFriendly: true,
    furnished: false,
    utilities: ['الكهرباء', 'المياه', 'الغاز'],
    amenities: ['سطح قابل للتجهيز', 'غرفة دراسة', 'نظام كاميرات', 'مخزن مستقل'],
    agentName: 'ليان المطيري',
    agentPhone: '+966 56 221 4433',
    agentEmail: 'layan.almutairi@safaqat24.sa',
    dateAdded: '2024-12-20',
    status: 'available',
    featured: false,
    pricePerSqft: calculatePricePerSqft(1120000, 3120),
    lastUpdated: '2025-01-28'
  },
  {
    id: 'ksa-riyadh-04',
    title: 'استوديو مفروش في قلب الرياض المالي',
    price: 540000,
    bedrooms: 1,
    bathrooms: 1,
    sqft: 980,
    address: 'حي الملك عبدالله المالي، برج الوادي',
    city: 'الرياض',
    state: 'منطقة الرياض',
    zipCode: '11564',
    latitude: 24.7743,
    longitude: 46.7386,
    imageUrl: 'https://images.unsplash.com/photo-1523217582562-09d0def993a6?auto=format&fit=crop&w=1200&q=80',
    galleryImages: [
      'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=1200&q=80'
    ],
    description: 'استوديو أنيق ومجهز بالكامل مناسب لرجال الأعمال، يضم مطبخاً متكاملاً ومساحة عمل خاصة وإطلالة على الحي المالي.',
    propertyType: 'Studio',
    yearBuilt: 2023,
    parking: true,
    coveredParking: true,
    parkingSpaces: 1,
    petFriendly: false,
    furnished: true,
    utilities: ['الكهرباء', 'المياه', 'الإنترنت الفائق'],
    amenities: ['صالة استقبال', 'قاعات اجتماعات', 'تنظيف أسبوعي', 'نادي صحي'],
    agentName: 'أحمد الدوسري',
    agentPhone: '+966 55 667 8891',
    agentEmail: 'ahmed.aldosari@safaqat24.sa',
    dateAdded: '2025-02-05',
    status: 'available',
    featured: false,
    pricePerSqft: calculatePricePerSqft(540000, 980),
    lastUpdated: '2025-02-10'
  }
];

export const sortPropertiesByPrice = (properties: Property[], ascending: boolean = true): Property[] => {
  return [...properties].sort((a, b) => ascending ? a.price - b.price : b.price - a.price);
};

export const sortPropertiesByDate = (properties: Property[], newest: boolean = true): Property[] => {
  return [...properties].sort((a, b) => {
    const dateA = new Date(a.dateAdded).getTime();
    const dateB = new Date(b.dateAdded).getTime();
    return newest ? dateB - dateA : dateA - dateB;
  });
};
